import { UHttp } from '../../utils/UHttp';
import { Option } from './option-model';

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

export interface OptionCategory {
  id: string;
  name: string;
  description?: string;
  serviceId: string;
  isRequired: boolean;
  isMultipleChoice: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  options?: Option[];
}

export interface CategoryResponse<T = any> {
  success: boolean;
  message: string;
  categoryId?: string;
  data?: T;
}

export interface OptionCategoriesResponse {
  success: boolean;
  message: string;
  data: OptionCategory[];
}

export interface CreateOptionCategoryData {
  name: string;
  description: string;
  serviceId: string;
  isRequired: boolean;
  isMultipleChoice: boolean;
}

export interface UpdateOptionCategoryData {
  name?: string;
  description?: string;
  isRequired?: boolean;
  isMultipleChoice?: boolean;
}

export class OptionCategoryModel {
  private static instance: OptionCategoryModel;

  private constructor() {}

  public static getInstance(): OptionCategoryModel {
    if (!OptionCategoryModel.instance) {
      OptionCategoryModel.instance = new OptionCategoryModel();
    }
    return OptionCategoryModel.instance;
  }

  public async getCategories(serviceId: string): Promise<OptionCategoriesResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/option-category/service/${serviceId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error('Error OptionCategoryModel.getCategories:', error);
      throw error;
    }
  }

  public async getCategoryById(categoryId: string): Promise<CategoryResponse<OptionCategory>> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/option-category/detail/${categoryId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionCategoryModel.getCategoryById ${categoryId}:`, error);
      throw error;
    }
  }

  public async createCategory(categoryData: CreateOptionCategoryData): Promise<CategoryResponse<OptionCategory>> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/option-category`,
        categoryData,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error('Error OptionCategoryModel.createCategory:', error);
      throw error;
    }
  }

  public async updateCategory(categoryId: string, categoryData: UpdateOptionCategoryData): Promise<CategoryResponse<OptionCategory>> {
    try {
      const response = await UHttp.put(
        `${apiUrlServer}/api/option-category/update/${categoryId}`,
        categoryData,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionCategoryModel.updateCategory ${categoryId}:`, error);
      throw error;
    }
  }

  public async toggleCategoryStatus(categoryId: string, isActive: boolean): Promise<CategoryResponse<OptionCategory>> {
    try {
      let response;
      if (isActive) {
        response = await UHttp.put(
          `${apiUrlServer}/api/option-category/enable/${categoryId}`,
          {},
          UHttp.authHeaders()
        );
      } else {
        response = await UHttp.delete(
          `${apiUrlServer}/api/option-category/disable/${categoryId}`,
          UHttp.authHeaders()
        );
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionCategoryModel.toggleCategoryStatus ${categoryId}:`, error);
      throw error;
    }
  }

  public async deleteCategory(categoryId: string): Promise<CategoryResponse> {
    try {
      const response = await UHttp.delete(
        `${apiUrlServer}/api/option-category/delete/${categoryId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionCategoryModel.deleteCategory ${categoryId}:`, error);
      throw error;
    }
  }
}
